import React, { useEffect, useState } from "react";
import Layout from "../Layout/Layout";
import "../css/threads.css";

import TribeContent from "../Components/Tribe/TribeContent";
import ManagePinGroupModal from "../Components/Tribe/PinGroupModal";
import { getProfile } from "../actions/login";
import CreateGroupModal from "../Components/Tribe/CreateGroupModal";
import AddMemberGroupModal from "../Components/AddMemberGroupModal";
import { useNavigate } from "react-router-dom";
import { api } from "../actions/api";
import GroupDetailsSidebar from "../Components/Tribe/TribeGroupSideBar";
import { toast } from "react-toastify";
import useTribePostStore from "../zustand-stores/TribePostStore";
import { useLocation } from "react-router-dom";
import LiveRelativeTime from "../Components/Tribe/LiveRelativeTime";
import { humanizeDate } from "../helpers/DateHelper";

export default function Tribe() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(0);
  const [selectedGroupDetails, setSelectedGroupDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [activeFilter, setActiveFilter] = useState("all");
  const [userProfile, setUserProfile] = useState(null);
  const [groups, setGroups] = useState([]);
  const [showIndividualPost, setShowIndividualPost] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [groupMeta, setGroupMeta] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreGroups, setHasMoreGroups] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [memberProfileRequest, setMemberProfileRequest] = useState(null);
  // const [selectedCommentId, setSelectedCommentId] = useState(null);
  // const [openCommentBox, setOpenCommentBox] = useState(false);

  const { refetchPost } = useTribePostStore();

  useEffect(() => {
    if (location.state?.selectedGroup && location.state?.selectedPost) {
      const { selectedGroup, selectedPost, selectedCommentId, openCommentBox } =
        location.state;

      // Set the selected group
      setSelectedGroup(Number(selectedGroup));

      // Set the selected post
      setSelectedPostId(Number(selectedPost));

      // setSelectedCommentId(selectedCommentId);

      // setOpenCommentBox(openCommentBox);

      // Clear the navigation state to prevent re-triggering
      navigate("/tribe", { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profile = await getProfile();
        setUserProfile(profile);
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const isCoach = userProfile?.is_coach;
  // const isCoach = false;
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };
  const handleSidebarMemberProfileClick = (member) => {
    if (!member?.user?.id) return;

    setMemberProfileRequest((prev) => ({
      member,
      requestId: (prev?.requestId || 0) + 1,
    }));
  };
  const [filterLoading, setFilterLoading] = useState(false);

  const fetchGroups = async (page = 1, append = false) => {
    try {
      setFilterLoading(!append);
      setIsLoadingMore(append);

      const res = await api.get("user/tribe-group", {
        params: {
          search: searchQuery,
          show_group:
            activeFilter === "all"
              ? "All"
              : activeFilter === "createdByYou"
                ? "Created_by_you"
                : "Admin_only",
          per_page: 10,
          page: page,
        },
      });

      const newGroups = res.data.data.groups;
      const meta = res.data.data.meta;

      setGroups((prev) => (append ? [...prev, ...newGroups] : newGroups));
      setGroupMeta(meta);
      setCurrentPage(meta.current_page);
      setHasMoreGroups(meta.current_page < meta.last_page);
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setFilterLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (userProfile) {
      setCurrentPage(1);
      setHasMoreGroups(true);
      fetchGroups(1, false);
    }
  }, [activeFilter, userProfile]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (userProfile) {
        setCurrentPage(1);
        setHasMoreGroups(true);
        fetchGroups(1, false);
      }
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const handleScroll = (e) => {
    const el = e.target;

    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;

    if (
      scrollTop + clientHeight >= scrollHeight - 10 &&
      hasMoreGroups &&
      !isLoadingMore
    ) {
      fetchGroups(currentPage + 1, true);
    }
  };

  // pin toggle function
  const handlePinToggle = async (groupId) => {
    try {
      const res = await api.post(`user/pin-group?id=${groupId}`);

      setGroups(
        groups.map((group) =>
          group.id === groupId
            ? { ...group, is_pinned: !group.is_pinned }
            : group,
        ),
      );

      toast.success(res.data.message);
    } catch (error) {
      console.error("Error toggling pin status:", error);
      toast.error("Failed to toggle pin status. Please try again.");
    }
  };

  const fetchGroupDetails = async () => {
    if (selectedGroup === null || typeof selectedGroup !== "number") {
      setSelectedGroupDetails(null);
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    setSelectedGroupDetails(null);

    setShowIndividualPost(false);
    if (selectedGroup === 0) {
      try {
        const postsRes = await api.get(`/user/post-feed/posts`, {
          params: { per_page: 10, page: 1 },
        });

        const mediaRes = await api.get(`/user/post-feed/medias`, {
          params: { per_page: 20, page: 1 },
        });

        const savedPostsRes = await api.get(`/user/post-feed/saved-posts`, {
          params: { per_page: 10, page: 1 },
        });

        setSelectedGroupDetails({
          posts: postsRes.data.data.posts,
          media: mediaRes.data.data.medias,
          savedPosts: savedPostsRes.data.data.saved_posts,
          meta: {
            posts: postsRes.data.data.meta,
            media: mediaRes.data.data.meta,
            savedPosts: savedPostsRes.data.data.meta,
          },
        });
      } catch (error) {
        console.error("Failed to fetch group details", error);
      } finally {
        setDetailLoading(false);
      }
    } else {
      try {
        const aboutRes = await api.get(`user/group/about/${selectedGroup}`);

        if (selectedGroup !== aboutRes.data.data.about.id) {
          setDetailLoading(false); // Ensure loading state is reset if ID mismatch
          return;
        }

        const postsRes = await api.get(`user/group/posts/${selectedGroup}`, {
          params: { per_page: 10, page: 1 },
        });

        const mediaRes = await api.get(`user/group/medias/${selectedGroup}`, {
          params: { per_page: 20, page: 1 },
        });

        const savedPostsRes = await api.get(
          `user/group/saved-posts/${selectedGroup}`,
          {
            params: { per_page: 10, page: 1 },
          },
        );

        setSelectedGroupDetails({
          about: aboutRes.data.data.about,
          posts: postsRes.data.data.posts,
          media: mediaRes.data.data.medias,
          savedPosts: savedPostsRes.data.data.saved_posts,
          meta: {
            posts: postsRes.data.data.meta,
            media: mediaRes.data.data.meta,
            savedPosts: savedPostsRes.data.data.meta,
          },
        });
      } catch (error) {
        console.error("Failed to fetch group details", error);
      } finally {
        setDetailLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchGroupDetails();
  }, [selectedGroup, refetchPost]);

  const handlePost = (newPostData) => {
    const isObject = newPostData && typeof newPostData === "object";
    const targetGroupId = isObject ? newPostData.group_id : newPostData;

    if (isObject) {
      // Update the latest_post_time for the relevant group(s) immediately in the sidebar
      if (targetGroupId && targetGroupId !== 0) {
        setGroups((prev) =>
          prev.map((group) =>
            group.id === targetGroupId
              ? {
                  ...group,
                  latest_post_time: "Just now",
                  latest_post_at:
                    newPostData.created_at || new Date().toISOString(),
                }
              : group,
          ),
        );
      }

      // Update selectedGroupDetails manually to avoid a full fetch refresh
      setSelectedGroupDetails((prev) => {
        if (!prev) return prev;

        // Only add to the current view if it belongs there (Feed or same Group)
        const shouldAdd =
          selectedGroup === 0 || selectedGroup === targetGroupId;
        if (!shouldAdd) return prev;

        // Check if it's already there (to handle updates or prevent duplicates)
        const postExists = prev.posts?.some((p) => p.id === newPostData.id);
        if (postExists) {
          return {
            ...prev,
            posts: prev.posts.map((p) =>
              p.id === newPostData.id ? { ...p, ...newPostData } : p,
            ),
          };
        }

        return {
          ...prev,
          posts: [newPostData, ...(prev.posts || [])],
        };
      });
    } else {
      // Fallback for calls without a post object (e.g. from legacy components or simple triggers)
      fetchGroupDetails();

      const groupIdToUpdate = targetGroupId || selectedGroup;
      if (groupIdToUpdate && groupIdToUpdate !== 0) {
        setGroups((prev) =>
          prev.map((group) =>
            group.id === groupIdToUpdate
              ? {
                  ...group,
                  latest_post_time: "Just now",
                  latest_post_at: new Date().toISOString(),
                }
              : group,
          ),
        );
      }
    }
  };

  // Sync latest post timestamp to groups list when group details are fetched
  useEffect(() => {
    // Only sync if the details belong to the currently selected group
    if (
      selectedGroupDetails?.posts?.length > 0 &&
      selectedGroup !== 0 &&
      selectedGroupDetails?.about?.id === selectedGroup
    ) {
      // Find the first post that isn't pinned (by anyone or current user)
      const latestNonPinnedPost = selectedGroupDetails.posts.find(
        (post) =>
          !post.pinned_by_super_admin &&
          !post.pinned_by_group_admin &&
          !post.is_pinned_by_current_user &&
          !post.pinned_by_type,
      );

      if (latestNonPinnedPost) {
        setGroups((prev) => {
          const groupIndex = prev.findIndex((g) => g.id === selectedGroup);
          if (groupIndex === -1) return prev;

          // Skip if timestamp is already synced and no change
          if (
            prev[groupIndex].latest_post_at === latestNonPinnedPost.created_at
          )
            return prev;

          const newGroups = [...prev];
          newGroups[groupIndex] = {
            ...newGroups[groupIndex],
            latest_post_at: latestNonPinnedPost.created_at,
            latest_post_time: humanizeDate(latestNonPinnedPost.created_at),
          };
          return newGroups;
        });
      }
    }
  }, [selectedGroupDetails, selectedGroup]);
  //remove member function
  const handleRemoveMember = async (userId) => {
    try {
      await api.post("user/remove-member", {
        group_id: selectedGroup,
        member_id: userId,
      });
      toast.success("Member removed successfully");

      // Refresh group details after removal
      // const res = await api.get(`user/tribe-group/${selectedGroup}`);
      // setSelectedGroupDetails(res.data.data.group_details);

      try {
        // Fetch about details
        const aboutRes = await api.get(`user/group/about/${selectedGroup}`);

        // Fetch posts
        const postsRes = await api.get(`user/group/posts/${selectedGroup}`, {
          params: { per_page: 10, page: 1 },
        });

        // Fetch media
        const mediaRes = await api.get(`user/group/medias/${selectedGroup}`, {
          params: { per_page: 10, page: 1 },
        });

        // Fetch saved posts
        const savedPostsRes = await api.get(
          `user/group/saved-posts/${selectedGroup}`,
          {
            params: { per_page: 10, page: 1 },
          },
        );

        setSelectedGroupDetails({
          about: aboutRes.data.data.about,
          posts: postsRes.data.data.posts,
          media: mediaRes.data.data.medias,
          savedPosts: savedPostsRes.data.data.saved_posts,
          meta: {
            posts: postsRes.data.data.meta,
            media: mediaRes.data.data.meta,
            savedPosts: savedPostsRes.data.data.meta,
          },
        });
      } catch (error) {
        console.error("Failed to fetch group details", error);
      }
    } catch (error) {
      console.error("Failed to remove member:", error);
      toast.error("Failed to remove member. Please try again.");
    }
  };

  const gotoLeaderboard = () => {
    window.location.href = "/leaderboards";
  };

  const handleGroupClicked = (groupId) => {
    setSelectedGroup(groupId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-content">
          <div className="text-center py-5">
            <div className="loader-container">
              <div className="loader"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content chat-main">
        <div className="setting-content">
          <div className="d-flex align-items-center gap-2 pages-title-container">
            <img className="sidebar-toggle" src="icons/menu.svg" />
            <h1 className="pages-header text-heading-h2 mb-0">Tribe</h1>
          </div>
          <div className="user-chats-card users-chat-card-components mb-3 mb-sm-4">
            <div className="d-flex w-100">
              <div className="chat-sidebar active-option">
                {/* post create */}
                <div className="chats-search d-flex align-items-center justify-content-between">
                  <input
                    className="header-search-1 text-caption-c2"
                    type="text"
                    placeholder="Search Tribe"
                    style={isCoach ? { width: "194px" } : {}}
                    onChange={handleSearch}
                  />
                  {isCoach && (
                    <div
                      className="d-flex align-items-center gap-1"
                      style={{ cursor: "pointer", marginLeft: "26px" }}
                      data-bs-toggle="modal"
                      data-bs-target="#createPostModal"
                    >
                      <img src="icons/create-pencil-icon.svg" alt="create" />
                      <span
                        style={{
                          color: "#008CFF",
                          fontWeight: 400,
                          marginLeft: "6px",
                        }}
                      >
                        Create
                      </span>
                    </div>
                  )}
                </div>

                <div
                  className="d-flex gap-2 gap-md-3 text-body-b4 chat-btn-container"
                  style={{ marginBottom: "24px" }}
                >
                  <button
                    className={`chat-all-btn chat-all-btn1${activeFilter === "all" ? "active-filter" : ""}`}
                    style={{
                      width: "46px",
                      height: "30px",
                      whiteSpace: "nowrap",
                      border: "1px solid white",
                    }}
                    onClick={() => setActiveFilter("all")}
                  >
                    All
                  </button>

                  {isCoach && (
                    <button
                      className={`btn-1 btn-1-basic chat-btn-1-basic chat-btn-1-basic1 ${activeFilter === "createdByYou" ? "active-filter" : ""}`}
                      style={{
                        width: "125px",
                        height: "30px",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          activeFilter === "createdByYou"
                            ? "#fff"
                            : "transparent",
                        color:
                          activeFilter === "createdByYou" ? "#1c2a37" : "#fff",
                        border: "1px solid white",
                      }}
                      onClick={() => setActiveFilter("createdByYou")}
                    >
                      Created by You
                    </button>
                  )}

                  <button
                    className={`btn-1 btn-1-basic chat-btn-1-basic chat-btn-1-basic1 ${activeFilter === "admin" ? "active-filter" : ""}`}
                    style={{
                      width: "90px",
                      height: "30px",
                      whiteSpace: "nowrap",
                      backgroundColor:
                        activeFilter === "admin" ? "#fff" : "transparent",
                      color: activeFilter === "admin" ? "#1c2a37" : "#fff",
                      border: "1px solid white",
                    }}
                    onClick={() => {
                      setFilterLoading(true);
                      setActiveFilter("admin");
                    }}
                  >
                    Admin Only
                  </button>
                </div>
                <div
                  className="text-white"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor:
                      selectedGroup === 0 ? "#1b2c3a" : "transparent",
                    padding:
                      selectedGroup === 0
                        ? "14px 8px 10px 8px"
                        : "14px 0px 10px 0px",
                    borderRadius: selectedGroup === 0 ? "12px" : "0",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedGroup(0)}
                >
                  <img
                    src={
                      selectedGroup === 0
                        ? "icons/post-selected.svg"
                        : "icons/post-icons.svg"
                    }
                    alt="post-icon"
                  />
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      marginLeft: "14px",
                      cursor: "pointer",
                      color: selectedGroup === 0 ? "#008cff" : "white",
                    }}
                  >
                    Posts
                  </span>
                </div>

                <div
                  className="text-white"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "28px",
                    backgroundColor: "transparent",
                    padding: "14px 8px 10px 8px",
                    borderRadius: "12px",
                    cursor: "pointer",
                  }}
                  onClick={() => gotoLeaderboard()}
                >
                  <img
                    src={"icons/medal.svg"}
                    alt="leaderboard-icon"
                    style={{ width: 32, height: 32 }}
                  />
                  <span
                    className={{}}
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      marginLeft: "14px",
                      cursor: "pointer",
                      color: "white",
                    }}
                  >
                    Leaderboard
                  </span>
                </div>

                <div
                  className="d-flex flex-column flex-grow-1 no-scrollbar"
                  onScroll={handleScroll}
                  style={{
                    height: "0px",
                    minHeight: "0px",
                  }}
                >
                  <div
                    className="flex-grow-0-important flex-grow-1"
                    style={{ marginBottom: "8px" }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "600",
                        marginBottom: "8px",
                      }}
                    >
                      <span>Pinned Groups</span>
                    </div>
                    {groups
                      .filter((group) => group.is_pinned)
                      .map((group) => (
                        <div
                          key={`pinned-${group.id}`}
                          className={`chat-item ${selectedGroup === group.id ? "active-group" : ""}`}
                          onClick={() => setSelectedGroup(group.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="d-flex gap-2 gap-md-3">
                              <img
                                src={group.image || "/icons/chat-liam.svg"}
                                className="profile-img"
                                alt={group.name}
                              />
                              <div
                                className="d-flex flex-column "
                                style={{ gap: "2px" }}
                              >
                                <div className="d-flex align-items-center">
                                  <span
                                    className="text-body-b1 group-name-big"
                                    title={group.name}
                                  >
                                    {group.name}
                                    {group.access_type === "Admin Only" && (
                                      <img
                                        src="/icons/admin-only-icon.svg"
                                        alt="Admin Only"
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          marginLeft: "8px",
                                        }}
                                      />
                                    )}
                                  </span>
                                </div>
                                <p className="text-body-b4 mb-0">
                                  Members: {group.member_count}
                                </p>
                              </div>
                            </div>
                            <div>
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePinToggle(group.id);
                                }}
                              >
                                <path
                                  d="M11.9694 3.40949C12.6225 2.97412 12.949 2.75643 13.3048 2.79166C13.6606 2.82689 13.938 3.10439 14.493 3.65938L16.3409 5.50729C16.8959 6.06228 17.1734 6.33978 17.2087 6.69555C17.2439 7.05132 17.0262 7.37785 16.5908 8.0309L15.3685 9.86435C15.0462 10.3479 14.885 10.5896 14.7428 10.8403C14.418 11.413 14.1651 12.0235 13.9898 12.6582C13.9131 12.936 13.8561 13.2209 13.7422 13.7907L13.583 14.5865C13.4652 15.1758 12.7823 15.4516 12.2882 15.1096C9.39777 13.1085 6.89184 10.6026 4.89075 7.71209C4.54868 7.21799 4.82452 6.53516 5.41381 6.4173L6.20961 6.25814C6.77944 6.14418 7.06436 6.08719 7.34212 6.0105C7.97678 5.83526 8.58737 5.58234 9.16005 5.25749C9.4107 5.11531 9.65246 4.95413 10.136 4.63179L11.9694 3.40949Z"
                                  fill="#7E8897"
                                  stroke="#7E8897"
                                  strokeWidth="2"
                                />
                                <path
                                  d="M4.16699 15.834L7.91699 12.084"
                                  stroke="#7E8897"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                              {group.id === selectedGroup &&
                              detailLoading ? null : (
                                <LiveRelativeTime
                                  date={group.latest_post_at}
                                  fallback={group.latest_post_time}
                                  className="text-small-s2"
                                  style={{ marginLeft: "8px" }}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                    <div
                      className={`chat-item ${selectedGroup === "group1" ? "active-group" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <div
                        className="d-flex  align-items-center"
                        style={{ height: "100%" }}
                      >
                        <div
                          className="d-flex gap-2 gap-md-3 align-items-center"
                          style={{ color: "#008CFF", cursor: "pointer" }}
                          data-bs-toggle="modal"
                          data-bs-target="#managePinGroupModal"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <svg
                            width="40"
                            height="40"
                            viewBox="0 0 40 40"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="20" cy="20" r="20" fill="#008CFF" />
                            <path
                              d="M20.0029 15.209C20.3232 15.2091 20.5868 15.4502 20.623 15.7607L20.6279 15.834V19.376H24.1689C24.5141 19.376 24.7939 19.6558 24.7939 20.001C24.7938 20.3213 24.5529 20.585 24.2422 20.6211L24.1689 20.626H20.6279V24.167C20.6279 24.5121 20.348 24.7918 20.0029 24.792C19.6824 24.792 19.418 24.5511 19.3818 24.2402L19.3779 24.167V20.626H15.8359C15.4909 20.626 15.2111 20.346 15.2109 20.001C15.2109 19.6806 15.452 19.4161 15.7627 19.3799L15.8359 19.376H19.3779V15.834C19.3779 15.4888 19.6578 15.209 20.0029 15.209Z"
                              fill="white"
                            />
                          </svg>
                          <span style={{ fontSize: "18px" }}>
                            Add pinned group
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "600",
                        marginBottom: "8px",
                      }}
                    >
                      <span>Groups</span>
                    </div>
                    {filterLoading ? (
                      <div className="text-center py-3">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      groups
                        .filter((group) => !group.is_pinned)
                        .map((group) => (
                          <div
                            key={`group-${group.id}`}
                            className={`chat-item ${selectedGroup === group.id ? "active-group" : ""}`}
                            onClick={() => setSelectedGroup(group.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="d-flex gap-2 gap-md-3">
                                <img
                                  src={group.image || "/icons/chat-liam.svg"}
                                  className="profile-img"
                                  alt={group.name}
                                />
                                <div
                                  className="d-flex flex-column "
                                  style={{ gap: "2px" }}
                                >
                                  <div className="d-flex align-items-center">
                                    <span
                                      className="text-body-b1 group-name-big"
                                      title={group.name}
                                    >
                                      {group.name}
                                      {group.access_type === "Admin Only" && (
                                        <img
                                          src="/icons/admin-only-icon.svg"
                                          alt="Admin Only"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            marginLeft: "8px",
                                          }}
                                        />
                                      )}
                                    </span>
                                  </div>
                                  <p className="text-body-b4 mb-0">
                                    Members: {group.member_count}
                                  </p>
                                </div>
                              </div>
                              <div>
                                {group.id === selectedGroup &&
                                detailLoading ? null : (
                                  <LiveRelativeTime
                                    date={group.latest_post_at}
                                    fallback={group.latest_post_time}
                                    className="text-small-s2"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                  {isLoadingMore && (
                    <div className="text-center py-2">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      />
                    </div>
                  )}
                </div>
              </div>
              {/* left side completed */}

              {/* main content*/}
              {selectedGroup !== null ? (
                selectedGroupDetails ? (
                  selectedGroup === 0 ? (
                    <TribeContent
                      selectedGroup={selectedGroup}
                      isAdminOnly={
                        selectedGroupDetails?.about?.access_type ===
                        "Admin Only"
                      }
                      isCurrentUserAdmin={selectedGroupDetails?.about?.is_admin}
                      isPost={selectedGroup === 0}
                      groupDetails={selectedGroupDetails}
                      userProfile={userProfile}
                      onShowIndividualPostChange={(val) =>
                        setShowIndividualPost(val)
                      }
                      onPost={handlePost}
                      selectedPostId={selectedPostId}
                      // selectedCommentId={selectedCommentId}
                      // openCommentBox={openCommentBox}
                      handleGroupClicked={handleGroupClicked}
                      memberProfileRequest={memberProfileRequest}
                      onMemberProfileRequestHandled={() =>
                        setMemberProfileRequest(null)
                      }
                    />
                  ) : (
                    <TribeContent
                      selectedGroup={selectedGroup}
                      isAdminOnly={
                        selectedGroupDetails?.about?.access_type ===
                        "Admin Only"
                      }
                      isCurrentUserAdmin={selectedGroupDetails?.about?.is_admin}
                      isPost={activeFilter === "post"}
                      groupDetails={selectedGroupDetails}
                      userProfile={userProfile}
                      onShowIndividualPostChange={(val) =>
                        setShowIndividualPost(val)
                      }
                      onPost={handlePost}
                      selectedPostId={selectedPostId}
                      // openCommentBox={openCommentBox}
                      // selectedCommentId={selectedCommentId}
                      handleGroupClicked={handleGroupClicked}
                      memberProfileRequest={memberProfileRequest}
                      onMemberProfileRequestHandled={() =>
                        setMemberProfileRequest(null)
                      }
                    />
                  )
                ) : (
                  // Show loader while loading group details
                  <div className="flex-grow-1 flex-column chats-contents active-chat">
                    <div className="text-center py-5">
                      <div className="loader-container">
                        <div className="loader"></div>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                // Show "No Group Selected" message when no group is selected
                <div
                  className="chat-content"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#FFFFFF",
                      marginBottom: "12px",
                    }}
                  >
                    No Group Selected
                  </h3>
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#7E8897",
                      maxWidth: "400px",
                      marginBottom: "24px",
                    }}
                  >
                    Select a group from the sidebar to view its content and
                    details.
                  </p>
                </div>
              )}

              {/* right side  */}
              {selectedGroup !== 0 &&
                !showIndividualPost &&
                selectedGroupDetails?.about?.id === selectedGroup && (
                  <GroupDetailsSidebar
                    selectedGroup={selectedGroup}
                    group={selectedGroupDetails?.about}
                    activeFilter={activeFilter}
                    onAddMemberClick={() => {}}
                    onRemoveMember={handleRemoveMember}
                    onMemberProfileClick={handleSidebarMemberProfileClick}
                    currentUserId={userProfile?.id}
                    onGroupDeleted={() => {
                      fetchGroups();
                      setSelectedGroup(null);
                    }}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
      {/* add pinned group modal */}
      <ManagePinGroupModal
        groups={groups}
        onPinToggle={handlePinToggle}
        setSearchQuery={setSearchQuery}
        filterLoading={filterLoading}
        onModalClose={() => {
          setSearchQuery("");
        }}
      />
      <CreateGroupModal
        onGroupCreated={() => {
          setActiveFilter("all");
          toast.success("Group created successfully!");
          window.location.reload();
        }}
      />
      <AddMemberGroupModal groupId={selectedGroup} />
    </Layout>
  );
}
